import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../api/axios';
import type { UsersState } from '../../types/store';
import type { AddUser, EditUser, User } from '../../types/user';

const initialState: UsersState = {
  data: [],
  status: 'idle',
  error: null,
};

export const fetchUsers = createAsyncThunk('users/fetchUsers', async () => {
  const response = await axios.get('users?limit=1000');
  return response.data;
});

export const addUser = createAsyncThunk(
  'users/addUser',
  async ({ data }: { data: AddUser }, { rejectWithValue }) => {
    try {
      const response = await axios.post('users', data);
      if (response.data.result) {
        return { data: response.data.result };
      }
      return { data: null };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const editUser = createAsyncThunk(
  'users/editUser',
  async ({ data, id }: { data: EditUser; id?: number }) => {
    // if id is set edit other user, otherwise edit current user
    const url = id ? 'users' : 'user';
    const response = await axios.post(url, data);
    if (response.data.result) {
      return { data: response.data.result };
    }
    return { data: null };
  },
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async ({ user_id }: { user_id: number }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`users?user_id=${user_id}`);
      return { user_id, response: response.data };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const usersSlice = createSlice({
  name: 'users',
  initialState: initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      // fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
        console.debug('Fetched users:', state.data);
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        console.debug('Cannot fetch users:', state.error);
      })
      // add user
      .addCase(addUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const data: User | null = action.payload.data;
        if (data) {
          state.data.push(data);
          console.debug('User added:', data);
        }
      })
      .addCase(addUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        console.debug('Cannot add user:', state.error);
      })
      // edit user
      .addCase(editUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const data: User | null = action.payload.data;
        if (data !== null && data.user_id) {
          const index = state.data.findIndex((el) => el.user_id === data.user_id);
          if (index !== -1) {
            state.data[index] = data;
            console.debug('User edited:', data);
          }
        }
      })
      .addCase(editUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        console.debug('Cannot edit user:', state.error);
      })
      // delete user
      .addCase(deleteUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = state.data.filter((user) => user.user_id !== action.payload.user_id);
        console.debug('User deleted:', action.payload.user_id);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
        console.debug('Cannot delete user:', state.error);
      });
  },
});

export const usersActions = usersSlice.actions;

export default usersSlice.reducer;
